"use client";
import { fetchSubscriptions } from "@/utils/api";
import { useEffect, useState } from "react";
import Badge from "../ui/badge/Badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "../ui/table";

interface Subscription {
  _id: string;
  id: number;
  email: string;
  frequency: string;
  unsubscribed: boolean;
  secondary_category_id: number | null;
  location_is_remote: boolean;
  location_type: string | null;
  jobseeker_id: number | null;
  created_at: string;
  updated_at: string;
  __v: number;
}

interface TableItem {
  id: number;
  name: string;
  variants: string;
  category: string;
  price: string;
  status: "Active" | "Unsubscribed";
}

// Function to mask email addresses
const maskEmail = (email: string): string => {
  const [localPart, domain] = email.split('@');
  if (localPart.length <= 2) return email;
  
  const firstTwoChars = localPart.substring(0, 4);
  const maskedPart = '*'.repeat(Math.min(localPart.length - 2, 3)); // Show max 3 asterisks
  return `${firstTwoChars}${maskedPart}@${domain}`;
};

// Transform subscriptions data to match the table structure
const transformSubscriptionsData = (subscriptions: Subscription[]): TableItem[] => {
  return subscriptions.map(subscription => ({
    id: subscription.id,
    name: maskEmail(subscription.email),
    variants: subscription.frequency,
    category: new Date(subscription.created_at).toLocaleDateString(),
    price: subscription.frequency,
    status: (subscription.unsubscribed ? "Unsubscribed" : "Active") as "Active" | "Unsubscribed",
  }));
};

export default function RecentSubscriptions() {
  const [subscriptions, setSubscriptions] = useState<TableItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadSubscriptions = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const data = await fetchSubscriptions({
          limit: 8,
          sortBy: 'created_at',
          sortOrder: 'desc'
        });

        if (data.success && data.data?.jobalerts && Array.isArray(data.data.jobalerts)) {
          const transformedData = transformSubscriptionsData(data.data.jobalerts);
          setSubscriptions(transformedData);
        } else {
          setError("Invalid data format received");
        }
      } catch (err) {
        console.error('Error fetching subscriptions:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch subscriptions');
      } finally {
        setLoading(false);
      }
    };

    loadSubscriptions();
  }, []);

  if (loading) {
    return (
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white px-4 pb-3 pt-4 dark:border-gray-800 dark:bg-white/[0.03] sm:px-6">
        <div className="flex flex-col gap-2 mb-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
              Recent Subscriptions
            </h3>
          </div>
        </div>
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          Loading subscriptions...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white px-4 pb-3 pt-4 dark:border-gray-800 dark:bg-white/[0.03] sm:px-6">
        <div className="flex flex-col gap-2 mb-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
              Recent Subscriptions
            </h3>
          </div>
        </div>
        <div className="text-center py-8 text-red-500 dark:text-red-400">
          Error: {error}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white px-4 pb-3 pt-4 dark:border-gray-800 dark:bg-white/[0.03] sm:px-6">
      <div className="flex flex-col gap-2 mb-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            Recent Subscriptions
          </h3>
        </div>

        <div className="flex items-center gap-3">
          <a 
            href="/subscriptions"
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-theme-sm font-medium text-gray-700 shadow-theme-xs hover:bg-gray-50 hover:text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03] dark:hover:text-gray-200"
          >
            See all
          </a>
        </div>
      </div>
      <div className="max-w-full overflow-x-auto">
        <Table>
          {/* Table Header */}
          <TableHeader className="border-gray-100 dark:border-gray-800 border-y">
            <TableRow>
              <TableCell
                isHeader
                className="py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
              >
                Email
              </TableCell>
              <TableCell
                isHeader
                className="py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
              >
                Frequency
              </TableCell>
              <TableCell
                isHeader
                className="py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
              >
                Created
              </TableCell>
              <TableCell
                isHeader
                className="py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
              >
                Status
              </TableCell>
            </TableRow>
          </TableHeader>

          {/* Table Body */}
          <TableBody className="divide-y divide-gray-100 dark:divide-gray-800">
            {subscriptions.length > 0 ? (
              subscriptions.map((item) => (
                <TableRow key={item.id} className="">
                  <TableCell className="py-3">
                    <div>
                      <p className="font-medium text-gray-800 text-theme-sm dark:text-white/90">
                        {item.name}
                      </p>
                      {/* <span className="text-gray-500 text-theme-xs dark:text-gray-400">
                        {item.variants}
                      </span> */}
                    </div>
                  </TableCell>
                  <TableCell className="py-3 text-gray-500 text-theme-sm dark:text-gray-400">
                    {item.price}
                  </TableCell>
                  <TableCell className="py-3 text-gray-500 text-theme-sm dark:text-gray-400">
                    {item.category}
                  </TableCell>
                  <TableCell className="py-3 text-gray-500 text-theme-sm dark:text-gray-400">
                    <Badge
                      size="sm"
                      color={item.status === "Active" ? "success" : "error"}
                    >
                      {item.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell className="py-8 text-center text-gray-500 dark:text-gray-400">
                  No subscriptions found
                </TableCell>
                <TableCell className="py-8 text-center text-gray-500 dark:text-gray-400">
                  &nbsp;
                </TableCell>
                <TableCell className="py-8 text-center text-gray-500 dark:text-gray-400">
                  &nbsp;
                </TableCell>
                <TableCell className="py-8 text-center text-gray-500 dark:text-gray-400">
                  &nbsp;
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
} 